import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, BookOpen, CalendarDays, UserCog, ClipboardList, CreditCard, Award, CheckSquare, BarChart3, LogOut, Menu, X, Search, Bell, LifeBuoy } from "lucide-react";
import { useEffect, useState } from "react";
import { adminNotificationService } from "@/services/notificationService";

const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "admin-nav-dashboard" },
  { to: "/admin/students", label: "Students", icon: Users, testid: "admin-nav-students" },
  { to: "/admin/courses", label: "Courses", icon: BookOpen, testid: "admin-nav-courses" },
  { to: "/admin/classes", label: "Classes", icon: CalendarDays, testid: "admin-nav-classes" },
  { to: "/admin/trainers", label: "Trainers", icon: UserCog, testid: "admin-nav-trainers" },
  { to: "/admin/enrolments", label: "Enrolments", icon: ClipboardList, testid: "admin-nav-enrolments" },
  { to: "/admin/payments", label: "Payments", icon: CreditCard, testid: "admin-nav-payments" },
  { to: "/admin/certificates", label: "Certificates", icon: Award, testid: "admin-nav-certs" },
  { to: "/admin/attendance", label: "Attendance", icon: CheckSquare, testid: "admin-nav-attendance" },
  { to: "/admin/support", label: "Support Tickets", icon: LifeBuoy, testid: "admin-nav-support" },
  { to: "/admin/reports", label: "Reports", icon: BarChart3, testid: "admin-nav-reports" },
  { to: "/admin/notifications", label: "Notifications", icon: Bell, testid: "admin-nav-notifications" },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [supportPending, setSupportPending] = useState(0);

  useEffect(() => {
    const loadUnreadCount = async () => {
      try {
        setUnreadNotifications(await adminNotificationService.getUnreadCount());
        const response = await fetch(`${process.env.REACT_APP_API_URL}/AdminSupport/pending-count`, { headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` } });
        if (response.ok) setSupportPending((await response.json()).count || 0);
      } catch (error) {
        console.error("Failed to load unread admin notifications", error);
      }
    };

    loadUnreadCount();
    window.addEventListener("notifications:updated", loadUnreadCount);
    return () => window.removeEventListener("notifications:updated", loadUnreadCount);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {open && <div className="lg:hidden fixed inset-0 bg-black/40 z-30" onClick={() => setOpen(false)} />}
      {/* Sidebar */}
      <aside className={`${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:sticky top-0 left-0 z-40 w-64 h-screen shrink-0 bg-[#0A2540] text-slate-300 flex flex-col transition-transform`}>
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <Link to="/admin/dashboard" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-white text-[#0A2540] flex items-center justify-center font-extrabold text-sm">WC</div>
            <div className="leading-tight">
              <div className="font-display font-extrabold text-white">White Card VIC</div>
              <div className="text-[10px] text-slate-400 -mt-0.5 uppercase tracking-wider">Admin Control Room</div>
            </div>
          </Link>
          <button onClick={() => setOpen(false)} className="lg:hidden text-white"><X className="w-5 h-5" /></button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1" data-testid="admin-sidebar">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={n.testid}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all border-l-4 ${isActive ? "border-orange-500 bg-white/10 text-white" : "border-transparent text-slate-300 hover:bg-white/5 hover:text-white"}`}>
              <span className="relative shrink-0">
                <n.icon className="w-4 h-4" />
                {((n.to === "/admin/notifications" && unreadNotifications > 0) || (n.to === "/admin/support" && supportPending > 0)) && (
                  <span className="absolute -right-1.5 -top-1.5 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-[#0A2540]" />
                )}
              </span>
              <span className="flex-1">{n.label}</span>
              {((n.to === "/admin/notifications" && unreadNotifications > 0) || (n.to === "/admin/support" && supportPending > 0)) && (
                <span className="min-w-5 h-5 px-1 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">
                  {n.to === "/admin/support" ? (supportPending > 99 ? "99+" : supportPending) : (unreadNotifications > 99 ? "99+" : unreadNotifications)}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-white/10">
          <button onClick={() => { localStorage.removeItem("token"); navigate("/", { replace: true }); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-white/5" data-testid="admin-logout">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="px-4 lg:px-8 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => setOpen(true)} className="lg:hidden p-1.5" aria-label="Menu"><Menu /></button>
              <div className="relative hidden md:block">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input placeholder="Search students, bookings, courses…" className="pl-9 pr-4 py-2 rounded-lg border border-slate-200 text-sm w-80 focus:outline-none focus:ring-2 focus:ring-[#0A2540]/10 focus:border-[#0A2540]" data-testid="admin-search-input" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/admin/notifications" className="relative p-2 hover:bg-slate-100 rounded-lg" aria-label="Notifications">
                <Bell className="w-5 h-5 text-slate-600" />
                {unreadNotifications > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 min-w-4 h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                    {unreadNotifications > 99 ? "99+" : unreadNotifications}
                  </span>
                )}
              </Link>
              <div className="flex items-center gap-2 pl-3 border-l border-slate-200">
                <div className="w-9 h-9 rounded-full bg-[#0A2540] text-white flex items-center justify-center font-bold text-sm">A</div>
                <div className="leading-tight hidden sm:block">
                  <div className="text-sm font-semibold">Admin</div>
                  <div className="text-xs text-slate-500">Super Admin</div>
                </div>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
